import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import LandingNav from "../marketing/LandingNav";
import {
  faArrowRight,
  faBuildingColumns,
  faChartLine,
  faChalkboardUser,
  faChildren,
  faCircleCheck,
  faLayerGroup,
  faLock,
  faUsers,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../auth/AuthContext";
import { resolvePostLoginRoute } from "../auth/post-login-route";
import { buildLoginPath } from "../auth/post-login-redirect";
import SiteFooter from "./legal/SiteFooter";
import AiLessonAnimation from "../marketing/AiLessonAnimation";
import IdeShowcase from "../marketing/IdeShowcase";
import LibraryShowcase from "../marketing/LibraryShowcase";
import CohortProgressShowcase from "../marketing/CohortProgressShowcase";
import FeatureIntents from "../marketing/FeatureIntents";
import {
  TEACHER_PAID_FEATURES_LINE,
  TEACHER_TRIAL_SUMMARY,
  TEACHER_TRIAL_VS_STRIPE_NOTE,
} from "../marketing/teacher-billing-copy";

const FEATURES: readonly { icon: typeof faWandMagicSparkles; title: string; body: string }[] = [
  {
    icon: faWandMagicSparkles,
    title: "Draft lessons with AI",
    body: "Describe a lesson in plain English and get starter code, a guided tour, and checks. Type @ to tag computer science standards so the draft aligns from the start.",
  },
  {
    icon: faChartLine,
    title: "Beginner to intermediate",
    body: "Start with single-file lessons for first-timers, then grow into multi-file projects for students who are ready for more.",
  },
  {
    icon: faUsers,
    title: "Multiple cohorts",
    body: "Run several classes side by side, each with its own roster, join code, and release schedule.",
  },
  {
    icon: faLayerGroup,
    title: "Build Reusable courses",
    body: "Order lessons into a blueprint once, then teach it every term. See which standards each lesson covers and what is still missing across the sequence.",
  },
];

const AUDIENCES: readonly {
  icon: typeof faChalkboardUser;
  title: string;
  points: readonly React.ReactNode[];
}[] = [
  {
    icon: faChalkboardUser,
    title: "For teachers",
    points: [
      "AI is optional. Lean on it as much or as little as you like.",
      "Bring your own teaching style at scale, adapting to what each student needs.",
      "See who's cruising and who's stuck at a glance.",
    ],
  },
  {
    icon: faChildren,
    title: "For parents",
    points: [
      "Real, transferable Python skills, not just drag-and-drop blocks.",
      "Runs in any browser, so kids can practice anywhere.",
      <>
        A privacy policy we&apos;re proud of: read how we{" "}
        <Link to="/privacy">protect your child&apos;s information</Link> and keep the focus on their
        learning.
      </>,
    ],
  },
  {
    icon: faBuildingColumns,
    title: "For school & district leaders",
    points: [
      "A low, predictable per-teacher cost.",
      "Harness the Chromebooks and devices you already have. Nothing new to buy or install.",
      "Progress dashboards that show real outcomes.",
    ],
  },
  {
    icon: faLock,
    title: "For IT administration",
    points: [
      "100% browser-based: nothing to install, great on Chromebooks.",
      "Code runs right in the browser, while student work is saved securely on our servers, so it's never stuck on one device.",
      "Sign in with the Google accounts students already use at school.",
    ],
  },
];

function Section({
  id,
  eyebrow,
  title,
  lede,
  children,
  tone,
}: {
  id?: string;
  eyebrow: string;
  title: string;
  lede?: string;
  children: React.ReactNode;
  tone?: "tint";
}) {
  return (
    <section id={id} className={`landing-section${tone === "tint" ? " landing-section--tint" : ""}`}>
      <div className="landing-section-inner">
        <header className="landing-section-head">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="landing-section-title">{title}</h2>
          {lede ? <p className="landing-section-lede">{lede}</p> : null}
        </header>
        {children}
      </div>
    </section>
  );
}

const TEACHER_LOGIN = buildLoginPath(null, "teacher");

export default function HomePage() {
  const { status, profile, identity } = useAuth();
  const location = useLocation();
  const authed = status !== "anonymous";

  const appHref =
    profile?.role === "teacher"
      ? "/teacher"
      : profile
        ? resolvePostLoginRoute(profile)
        : "/onboarding";

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const target = document.getElementById(id);
    if (!target) return;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
  }, [location.pathname, location.hash]);

  return (
    <div className="landing">
      <LandingNav />

      <main>
        <section className="landing-hero">
          <div className="landing-hero-inner">
            <div className="landing-hero-text">
              <p className="eyebrow">MsLeonard.com</p>
              <h1 className="landing-hero-title">
                Teach real Python, even if you&apos;re learning it too.
              </h1>
              <p className="landing-hero-lede">
                Code Create Slay is a browser-based Python classroom where AI helps you learn, build
                lessons, and run multiple classes, while your students write real code on any
                Chromebook. <strong>Free for students, forever.</strong>
              </p>
              <div className="landing-hero-actions">
                {authed ? (
                  <Link className="btn primary landing-cta" to={appHref}>
                    {profile?.role === "teacher" ? "Open teacher workspace" : "Go to app"}{" "}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                ) : (
                  <Link className="btn primary landing-cta" to={TEACHER_LOGIN}>
                    Start free <FontAwesomeIcon icon={faArrowRight} />
                  </Link>
                )}
                <a className="btn secondary landing-cta" href="#how-it-works">
                  See how it works
                </a>
              </div>
              {authed && identity?.email ? (
                <p className="landing-hero-signed muted small">
                  Signed in as <strong>{identity.email}</strong>
                </p>
              ) : (
                <p className="landing-hero-trust muted small">
                  No credit card to try it · Runs in the browser · Sign in with Google
                </p>
              )}
            </div>
            <div className="landing-hero-visual">
              <AiLessonAnimation />
            </div>
          </div>
        </section>

        <Section
          id="how-it-works"
          eyebrow="AI lesson studio"
          title="Go from a sentence to a ready-to-teach lesson"
          lede="Describe what you want students to learn. The AI drafts starter code, a guided tour, and automated checks. You stay in control and edit anything."
          tone="tint"
        >
          <div className="landing-aside">
            <ul className="landing-checklist">
              <li>
                <FontAwesomeIcon icon={faCircleCheck} /> Starter <code>main.py</code> scaffolded for the
                concept
              </li>
              <li>
                <FontAwesomeIcon icon={faCircleCheck} /> A step-by-step tour students follow in the IDE
              </li>
              <li>
                <FontAwesomeIcon icon={faCircleCheck} /> Validation checks that confirm the work
              </li>
              <li>
                <FontAwesomeIcon icon={faCircleCheck} /> Edit, refine, or regenerate. It&apos;s your lesson
              </li>
            </ul>
            <p className="muted small landing-note">
              This is a live, looping demo of the actual AI progress experience.
            </p>
          </div>
        </Section>

        <Section
          id="ide"
          eyebrow="The classroom IDE"
          title="One IDE that grows with your students"
          lede="Beginners get a focused single-file editor with a guided tour. When they're ready, the same workspace expands into a multi-file project, all running Python right in the browser."
        >
          <IdeShowcase />
        </Section>

        <Section
          id="features"
          eyebrow="Why teachers love it"
          title="Everything you need to run a coding class"
          lede="Tag computer science standards when you draft with AI, see which standards each lesson supports, and track coverage gaps across a course."
          tone="tint"
        >
          <figure className="landing-domain-model">
            <img
              src="/domain-model.jpg"
              alt="Diagram of lessons, courses, cohorts, and assignments in Code Create Slay"
              width={1200}
              height={680}
              loading="lazy"
              decoding="async"
            />
          </figure>
          <div className="landing-feature-grid landing-feature-grid--four">
            {FEATURES.map((f) => (
              <article key={f.title} className="landing-feature">
                <span className="landing-feature-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={f.icon} />
                </span>
                <h3 className="landing-feature-title">{f.title}</h3>
                <p className="landing-feature-body muted">{f.body}</p>
              </article>
            ))}
          </div>
        </Section>

        <Section
          id="library"
          eyebrow="Lesson library"
          title="Build a reusable library, little Python required"
          lede="Every lesson you create lands in a searchable library. AI drafts stay separate until you promote them. Hover a lesson's standards pill to see aligned codes, reuse lessons across classes, bundle them into courses, and publish favorites for other teachers."
        >
          <LibraryShowcase />
        </Section>

        <Section
          id="progress"
          eyebrow="Cohort progress"
          title="See exactly where every class stands"
          lede="Live dashboards show completion, who's in progress, and who needs help (per lesson and per cohort) so you know where to jump in."
          tone="tint"
        >
          <CohortProgressShowcase />
        </Section>

        <Section
          id="audiences"
          eyebrow="Built for your whole community"
          title="Friendly for teachers, parents, and administrators"
        >
          <div className="landing-audience-grid">
            {AUDIENCES.map((a) => (
              <article key={a.title} className="landing-audience">
                <span className="landing-audience-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={a.icon} />
                </span>
                <h3 className="landing-audience-title">{a.title}</h3>
                <ul className="landing-audience-points">
                  {a.points.map((p, i) => (
                    <li key={i}>
                      <FontAwesomeIcon icon={faCircleCheck} />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </Section>

        <Section
          id="pricing"
          eyebrow="Pricing"
          title="Students pay nothing. Schools pay a little."
          lede={`Code Create Slay is free for students, forever. A small fee from teachers and schools keeps the lights on, and unlocks ${TEACHER_PAID_FEATURES_LINE.toLowerCase()}.`}
          tone="tint"
        >
          <div className="landing-free-banner">
            <span className="landing-free-badge">
              <FontAwesomeIcon icon={faChildren} /> Always free for students
            </span>
            <p className="muted">
              Students never pay and never see a paywall. Pricing below is for the teacher account that
              runs the class.
            </p>
          </div>

          <div className="landing-pricing-grid">
            <article className="landing-plan">
              <h3 className="landing-plan-name">Monthly</h3>
              <p className="landing-plan-price">
                $19 <span className="landing-plan-period">/ month</span>
              </p>
              <p className="muted landing-plan-detail">
                {TEACHER_PAID_FEATURES_LINE}. Billed monthly, cancel any time.
              </p>
              <Link to={TEACHER_LOGIN} className="btn">
                Get started
              </Link>
            </article>

            <article className="landing-plan landing-plan--featured">
              <span className="landing-plan-badge">Best value</span>
              <h3 className="landing-plan-name">Annual</h3>
              <p className="landing-plan-price">
                $199 <span className="landing-plan-period">/ year</span>
              </p>
              <p className="muted landing-plan-detail">
                Everything in Monthly for about $16.60/month, billed once a year.
              </p>
              <Link to={TEACHER_LOGIN} className="btn primary">
                Get started
              </Link>
            </article>

            <article className="landing-plan landing-plan--trial">
              <h3 className="landing-plan-name">Free trial</h3>
              <p className="landing-plan-price landing-plan-price--free">$0</p>
              <p className="muted landing-plan-detail">{TEACHER_TRIAL_SUMMARY}</p>
              <Link to={TEACHER_LOGIN} className="btn secondary">
                Start the trial
              </Link>
            </article>
          </div>
          <p className="muted small landing-pricing-note">
            {TEACHER_TRIAL_VS_STRIPE_NOTE} Whole-school and district pricing available. Get in touch and
            we&apos;ll keep the per-teacher cost low.
          </p>
        </Section>

        <Section
          id="intents"
          eyebrow="What we're building toward"
          title="More learning, less of your time"
          lede="Real classroom stories are coming. Until then, here's what we're focused on building: features that help teachers maximize student learning without soaking up their personal time."
        >
          <FeatureIntents />
        </Section>

        <section className="landing-final-cta">
          <div className="landing-final-cta-inner">
            <h2 className="landing-final-cta-title">Ready to teach Python the slay way?</h2>
            <p className="landing-final-cta-lede">
              Free for students, friendly for teachers, and easy on IT. Sign in to start your 15-day free
              trial (no card required).
            </p>
            <Link className="btn primary landing-cta" to={authed ? appHref : TEACHER_LOGIN}>
              {authed ? "Open the app" : "Start free"} <FontAwesomeIcon icon={faArrowRight} />
            </Link>
          </div>
        </section>

        <div className="landing-footer-wrap">
          <SiteFooter />
        </div>
      </main>
    </div>
  );
}
